import type {
  AllocLogEntry,
  ApiKeyInfo,
  AvailableCombosResponse,
  BatchActionName,
  CapacityBlockGroup,
  CiJobDetail,
  CiJobOverview,
  CiLogFile,
  CiOverviewRow,
  CiRunDetail,
  CiRunsPage,
  CiTestHistoryEntry,
  CiTestResult,
  CiTrendPoint,
  CreateProductionMachineRequest,
  CreateSpotTargetRequest,
  CreateUserRequest,
  CryodCatalogCell,
  CryodEngineVersionOption,
  CryodFacets,
  CryodMatrixTestStatus,
  CryodOverviewParams,
  CryodOverviewRow,
  CryodRunRow,
  CryodSummary,
  CryodTestTriggerRequest,
  CryodTofaRecipe,
  CryodVersionOption,
  DailySpendEntry,
  DirectoryUser,
  GpuCluster,
  IdleSpotReaperState,
  K8sCluster,
  Machine,
  MachineMetrics,
  MachineRequest,
  MctestInfo,
  MctestTriggerResponse,
  NebiusRegionsState,
  NightlyConfig,
  NightlyConfigState,
  NightlyReaperState,
  PerfTriggerRequest,
  PreemptionsResponse,
  ProductionMachine,
  ProvisioningLogEntry,
  ScheduledRun,
  SpendSummary,
  SpendTrendPoint,
  SpotPoolAccessState,
  SpotStatisticsResponse,
  SpotTarget,
  TriggerOptions,
  UpdateProductionMachineRequest,
  UpdateSpotTargetRequest,
  User,
} from "./types";

function buildUrl(path: string, params?: Record<string, string>): string {
  const url = new URL(path, window.location.origin);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, value);
      }
    }
  }
  return url.toString();
}

// FastAPI puts the reason in `detail`; the status prefix stays (callers match on it).
async function apiError(resp: Response): Promise<Error> {
  const text = await resp.text();
  try {
    const { detail } = JSON.parse(text);
    return new Error(`${resp.status}: ${typeof detail === "string" ? detail : text}`);
  } catch {
    return new Error(`${resp.status}: ${text}`);
  }
}

async function fetchApi<T>(
  url: string,
  params?: Record<string, string>,
  { redirectOn401 = true }: { redirectOn401?: boolean } = {},
): Promise<T> {
  const resp = await fetch(buildUrl(url, params), { credentials: "include" });
  if (resp.status === 401) {
    if (redirectOn401 && !window.location.pathname.startsWith("/login")) {
      window.location.href = "/login";
    }
    throw new Error("Unauthorized");
  }
  if (!resp.ok) throw await apiError(resp);
  return resp.json();
}

async function postApi<T>(
  url: string,
  body?: unknown,
  { redirectOn401 = true }: { redirectOn401?: boolean } = {},
): Promise<T> {
  const resp = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });
  if (resp.status === 401) {
    if (redirectOn401 && !window.location.pathname.startsWith("/login")) {
      window.location.href = "/login";
    }
    throw new Error("Unauthorized");
  }
  if (!resp.ok) throw await apiError(resp);
  return resp.json();
}

async function putApi<T>(
  url: string,
  body: unknown,
  { redirectOn401 = true }: { redirectOn401?: boolean } = {},
): Promise<T> {
  const resp = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (resp.status === 401) {
    if (redirectOn401 && !window.location.pathname.startsWith("/login")) {
      window.location.href = "/login";
    }
    throw new Error("Unauthorized");
  }
  if (!resp.ok) throw await apiError(resp);
  return resp.json();
}

async function patchApi<T>(
  url: string,
  body: unknown,
  { redirectOn401 = true }: { redirectOn401?: boolean } = {},
): Promise<T> {
  const resp = await fetch(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (resp.status === 401) {
    if (redirectOn401 && !window.location.pathname.startsWith("/login")) {
      window.location.href = "/login";
    }
    throw new Error("Unauthorized");
  }
  if (!resp.ok) throw await apiError(resp);
  return resp.json();
}

async function deleteApi<T>(
  url: string,
  { redirectOn401 = true }: { redirectOn401?: boolean } = {},
): Promise<T> {
  const resp = await fetch(url, { method: "DELETE", credentials: "include" });
  if (resp.status === 401) {
    if (redirectOn401 && !window.location.pathname.startsWith("/login")) {
      window.location.href = "/login";
    }
    throw new Error("Unauthorized");
  }
  if (!resp.ok) throw await apiError(resp);
  return resp.json();
}

export const api = {
  getPreemptions: (params: Record<string, string>) =>
    fetchApi<PreemptionsResponse>("/api/preemptions", params),
  getSpotStatistics: (params: Record<string, string>) =>
    fetchApi<SpotStatisticsResponse>("/api/spot-statistics", params),
  // Auth
  login: (username: string, password: string) =>
    postApi<{ id: number; username: string; name: string }>(
      "/api/auth/login",
      { username, password },
      { redirectOn401: false },
    ),
  logout: () => postApi<{ ok: boolean }>("/api/auth/logout"),
  me: () => fetchApi<User>("/api/users/me", undefined, { redirectOn401: false }),

  // Machines
  listMachines: (params?: Record<string, string>) => fetchApi<Machine[]>("/api/machines", params),
  getMachine: (id: number) => fetchApi<Machine>(`/api/machines/${id}`),
  deleteMachine: (id: number) => deleteApi<Machine>(`/api/machines/${id}`),
  acquireMachine: (id: number) => postApi<Machine>(`/api/machines/${id}/acquire`),
  looseAcquireMachine: (id: number) => postApi<Machine>(`/api/machines/${id}/loose-acquire`),
  acquireMachinesBatch: (machineIds: number[]) =>
    postApi<{
      results: {
        id: number;
        ok: boolean;
        machine: Machine | null;
        error: string | null;
        was_pre_owned: boolean;
      }[];
      succeeded: number;
      failed: number;
    }>("/api/machines/acquire-batch", { machine_ids: machineIds }),
  releaseMachine: (id: number) => postApi<Machine>(`/api/machines/${id}/release`),
  rebootMachine: (id: number) => postApi<Machine>(`/api/machines/${id}/reboot`),
  requestMachine: (id: number) => postApi<MachineRequest>(`/api/machines/${id}/request`, undefined),
  requestMachinesBatch: (machineIds: number[]) =>
    postApi<{
      results: {
        id: number;
        ok: boolean;
        request: MachineRequest | null;
        error: string | null;
      }[];
      succeeded: number;
      failed: number;
    }>("/api/machines/request-batch", { machine_ids: machineIds }),
  cancelMachineRequest: (requestId: number) =>
    deleteApi<{ status: string }>(`/api/machine-requests/${requestId}`),
  listMyMachineRequests: (openOnly = true) =>
    fetchApi<MachineRequest[]>(`/api/machine-requests${openOnly ? "" : "?open_only=false"}`),
  batchAction: (
    machineIds: number[],
    action: BatchActionName,
    options?: { deleteAfterStop?: boolean },
  ) =>
    postApi<{
      results: {
        id: number;
        ok: boolean;
        machine: Machine | null;
        error: string | null;
      }[];
      succeeded: number;
      failed: number;
      batch_id: string;
    }>("/api/machines/batch", {
      machine_ids: machineIds,
      action,
      delete_after_stop: options?.deleteAfterStop ?? false,
    }),
  setNote: (machineIds: number[], note: string | null) =>
    postApi<{
      results: {
        id: number;
        ok: boolean;
        machine: Machine | null;
        error: string | null;
      }[];
      succeeded: number;
      failed: number;
    }>("/api/machines/set-note", { machine_ids: machineIds, note }),
  transferMachine: (id: number, userId: number) =>
    postApi<Machine>(`/api/machines/${id}/transfer?user_id=${userId}`),
  assignInferbot: (id: number) => postApi<Machine>(`/api/machines/${id}/assign-inferbot`),
  stopMachine: (id: number, deleteAfterStop?: boolean) =>
    postApi<Machine>(`/api/machines/${id}/stop${deleteAfterStop ? "?delete=true" : ""}`),
  startMachine: (id: number) => postApi<Machine>(`/api/machines/${id}/start`),
  skipEodRelease: (id: number) => postApi<Machine>(`/api/machines/${id}/skip-eod-release`),
  unskipEodRelease: (id: number) => deleteApi<Machine>(`/api/machines/${id}/skip-eod-release`),
  lockMachine: (id: number) => postApi<Machine>(`/api/machines/${id}/lock`),
  unlockMachine: (id: number) => deleteApi<Machine>(`/api/machines/${id}/lock`),
  markMachineDisposable: (id: number) => postApi<Machine>(`/api/machines/${id}/disposable`),
  // Same endpoint as above: the posted values replace the stored ones — an
  // empty list means anyone, a null window means takeable right away.
  setDisposableOptions: (
    id: number,
    { allowedUserIds, disposableFrom }: { allowedUserIds: number[]; disposableFrom: string | null },
  ) =>
    postApi<Machine>(`/api/machines/${id}/disposable`, {
      allowed_user_ids: allowedUserIds,
      disposable_from: disposableFrom,
    }),
  unmarkMachineDisposable: (id: number) => deleteApi<Machine>(`/api/machines/${id}/disposable`),
  protectMachine: (id: number) => postApi<Machine>(`/api/machines/${id}/skip-delete`),
  unprotectMachine: (id: number) => deleteApi<Machine>(`/api/machines/${id}/skip-delete`),
  markMachineProduction: (id: number) =>
    postApi<{ ok: boolean }>(`/api/machines/${id}/mark-production`),

  // Users
  listUsers: () => fetchApi<User[]>("/api/users"),
  listUserDirectory: () => fetchApi<DirectoryUser[]>("/api/users/directory"),
  createUser: (body: CreateUserRequest) => postApi<User>("/api/users", body),
  updateSshKey: (id: number, sshPubKey: string) =>
    putApi<User>(`/api/users/${id}/ssh-key`, { ssh_pub_key: sshPubKey }),
  updateAvatar: (id: number, avatarUrl: string | null) =>
    putApi<User>(`/api/users/${id}/avatar`, { avatar_url: avatarUrl }),

  // API Keys
  listApiKeys: (userId: number) => fetchApi<ApiKeyInfo[]>(`/api/users/${userId}/api-keys`),
  createApiKey: (userId: number, name: string) =>
    postApi<{ id: number; key: string; prefix: string; name: string }>(
      `/api/users/${userId}/api-keys`,
      { name },
    ),
  revokeApiKey: (userId: number, keyId: number) =>
    deleteApi<{ ok: boolean }>(`/api/users/${userId}/api-keys/${keyId}`),
  revealApiKey: (userId: number, keyId: number) =>
    postApi<{ id: number; key: string }>(`/api/users/${userId}/api-keys/${keyId}/reveal`),

  // Spot Targets
  listSpotTargets: () => fetchApi<SpotTarget[]>("/api/spot-targets"),
  createSpotTarget: (body: CreateSpotTargetRequest) =>
    postApi<SpotTarget>("/api/spot-targets", body),
  updateSpotTarget: (id: number, body: UpdateSpotTargetRequest) =>
    putApi<SpotTarget>(`/api/spot-targets/${id}`, body),
  deleteSpotTarget: (id: number) => deleteApi<{ ok: boolean }>(`/api/spot-targets/${id}`),
  ephemeralStartSpotTarget: (id: number, durationHours: number) =>
    postApi<SpotTarget>(`/api/spot-targets/${id}/ephemeral-start`, {
      duration_hours: durationHours,
    }),
  ephemeralStopSpotTarget: (id: number) =>
    postApi<SpotTarget>(`/api/spot-targets/${id}/ephemeral-stop`),
  listTargetMachines: (targetId: number) =>
    fetchApi<Machine[]>(`/api/spot-targets/${targetId}/machines`),
  getProvisioningLogs: (targetId: number) =>
    fetchApi<ProvisioningLogEntry[]>(`/api/spot-targets/${targetId}/logs`),
  getAvailableCombos: (provider: string, gpuType: string, gpuCount: number) =>
    fetchApi<AvailableCombosResponse>("/api/spot-targets/available-combos", {
      provider,
      gpu_type: gpuType,
      gpu_count: String(gpuCount),
    }),

  // Capacity block groups
  listCapacityBlockGroups: () => fetchApi<CapacityBlockGroup[]>("/api/capacity-block-groups"),
  syncCbgs: () => postApi<CapacityBlockGroup[]>("/api/capacity-block-groups/sync"),
  listCbgMachines: (cbgId: number) =>
    fetchApi<Machine[]>(`/api/capacity-block-groups/${cbgId}/machines`),
  startCbgMachine: (cbgId: number, machineId: number) =>
    postApi<{ status: string; machine_id: number }>(
      `/api/capacity-block-groups/${cbgId}/machines/${machineId}/start`,
    ),
  stopCbgMachine: (cbgId: number, machineId: number) =>
    postApi<{ status: string; machine_id: number }>(
      `/api/capacity-block-groups/${cbgId}/machines/${machineId}/stop`,
    ),

  // GPU clusters
  listGpuClusters: () => fetchApi<GpuCluster[]>("/api/gpu-clusters"),

  // K8s clusters
  listK8sClusters: () => fetchApi<K8sCluster[]>("/api/k8s-clusters"),
  getK8sCluster: (id: number) => fetchApi<K8sCluster>(`/api/k8s-clusters/${id}`),
  listK8sClusterMachines: (id: number) => fetchApi<Machine[]>(`/api/k8s-clusters/${id}/machines`),
  acquireK8sCluster: (id: number) => postApi<K8sCluster>(`/api/k8s-clusters/${id}/acquire`),
  releaseK8sCluster: (id: number) => postApi<K8sCluster>(`/api/k8s-clusters/${id}/release`),
  markK8sClusterProduction: (id: number) =>
    postApi<K8sCluster>(`/api/k8s-clusters/${id}/mark-production`),
  unmarkK8sClusterProduction: (id: number) =>
    postApi<K8sCluster>(`/api/k8s-clusters/${id}/unmark-production`),

  // Alloc log
  listAllocLog: (params?: Record<string, string>) =>
    fetchApi<AllocLogEntry[]>("/api/alloc-log", params),

  // Metrics
  getMachineMetrics: (machineId: number, range: string) =>
    fetchApi<MachineMetrics>(`/api/machines/${machineId}/metrics`, { range }),

  // Trigger jobs
  triggerJob: (jobId: string) => postApi<{ ok: boolean; job_id: string }>(`/api/trigger/${jobId}`),

  // On-demand mctest (the Trigger page)
  getMctestInfo: () => fetchApi<MctestInfo>("/api/mctest"),
  triggerMctest: (mooncakeRef: string, startAt: string | null) =>
    postApi<MctestTriggerResponse>("/api/mctest/trigger", {
      mooncake_ref: mooncakeRef,
      start_at: startAt,
    }),
  // Cryod test + performance test (the Trigger page)
  getTriggerOptions: () => fetchApi<TriggerOptions>("/api/ondemand/options"),
  triggerCryodTest: (body: CryodTestTriggerRequest) =>
    postApi<MctestTriggerResponse>("/api/ondemand/cryod-test", body),
  triggerPerf: (body: PerfTriggerRequest) =>
    postApi<MctestTriggerResponse>("/api/ondemand/perf", body),
  getNightlyConfig: (run: ScheduledRun) =>
    fetchApi<NightlyConfigState>(`/api/ondemand/${run}-config`),
  setNightlyConfig: (run: ScheduledRun, config: NightlyConfig) =>
    putApi<NightlyConfig>(`/api/ondemand/${run}-config`, config),
  // Stop an active job (its requester or an admin); it ends `cancelled`.
  cancelCiJob: (jobId: string) =>
    postApi<{ ok: boolean; state: string }>(
      `/api/cryod-ci/jobs/${encodeURIComponent(jobId)}/cancel`,
    ),

  // CI Jobs (orchestrated runs: nightly, release qualification, manual)
  listCiJobOverviews: (params?: Record<string, string>) =>
    fetchApi<CiJobOverview[]>("/api/cryod-ci/jobs-overview", params),
  getCiJob: (jobId: string) =>
    fetchApi<CiJobDetail>(`/api/cryod-ci/jobs/${encodeURIComponent(jobId)}`),

  // CI Results
  listCiRuns: (params?: Record<string, string>) =>
    fetchApi<CiRunsPage>("/api/criu-ci/runs", params),
  listCiRunTriggerUsers: () => fetchApi<string[]>("/api/criu-ci/runs/triggered-by"),
  getCiRun: (runId: string) => fetchApi<CiRunDetail>(`/api/criu-ci/runs/${runId}`),
  getCiTestResult: (runId: string, resultId: number) =>
    fetchApi<CiTestResult>(`/api/criu-ci/runs/${runId}/results/${resultId}`),
  listCiTestNames: () => fetchApi<string[]>("/api/criu-ci/test-names"),
  getCiOverview: (params: Record<string, string>) =>
    fetchApi<CiOverviewRow[]>("/api/criu-ci/overview", params),
  getCiTrends: (params: Record<string, string>) =>
    fetchApi<CiTrendPoint[]>("/api/criu-ci/trends", params),
  getCryodOverview: (params: CryodOverviewParams) => {
    const query = new URLSearchParams();
    query.set("group_by", params.group_by);
    if (params.sub_group_by) query.set("sub_group_by", params.sub_group_by);
    query.set("metric", params.metric);
    query.set("agg", params.agg);
    query.set("window", params.window);
    for (const filter of params.filters ?? []) query.append("filter", filter);
    return fetchApi<CryodOverviewRow[]>(`/api/criu-ci/cryod-overview?${query.toString()}`);
  },
  getCryodFacets: () => fetchApi<CryodFacets>("/api/criu-ci/cryod-facets"),
  getCryodSummary: (params: { window: string; filters?: string[] }) => {
    const query = new URLSearchParams({ window: params.window });
    for (const filter of params.filters ?? []) query.append("filter", filter);
    return fetchApi<CryodSummary>(`/api/criu-ci/cryod-summary?${query.toString()}`);
  },
  getCryodRuns: (params: {
    window: string;
    metric: string;
    filters?: string[];
    limit?: number;
  }) => {
    const query = new URLSearchParams({ window: params.window, metric: params.metric });
    if (params.limit) query.set("limit", String(params.limit));
    for (const filter of params.filters ?? []) query.append("filter", filter);
    return fetchApi<CryodRunRow[]>(`/api/criu-ci/cryod-runs?${query.toString()}`);
  },
  getCryodCatalog: () => fetchApi<CryodCatalogCell[]>("/api/criu-ci/cryod-catalog"),
  getCryodVersions: (params?: Record<string, string>) =>
    fetchApi<CryodVersionOption[]>("/api/criu-ci/cryod-versions", params),
  getCryodEngineVersions: (tag?: string, engine?: string) => {
    const params: Record<string, string> = {};
    if (tag) params.tag = tag;
    if (engine) params.engine = engine;
    return fetchApi<CryodEngineVersionOption[]>(
      "/api/criu-ci/cryod-engine-versions",
      Object.keys(params).length > 0 ? params : undefined,
    );
  },
  getCryodBuildRevisions: (params?: Record<string, string>) =>
    fetchApi<{ cryod: string[] }>("/api/criu-ci/cryod-build-revisions", params),
  getCryodTofa: (params: Record<string, string>) =>
    fetchApi<CryodTofaRecipe[]>("/api/criu-ci/cryod-tofa", params),
  getCryodMatrixStatus: (params: Record<string, string>) =>
    fetchApi<CryodMatrixTestStatus[]>("/api/criu-ci/cryod-matrix-status", params),
  getCiTestHistory: (testName: string, params?: Record<string, string>) =>
    fetchApi<CiTestHistoryEntry[]>(
      `/api/criu-ci/tests/${encodeURIComponent(testName)}/history`,
      params,
    ),
  listCiTestLogs: (runId: string, testName: string, version: string) =>
    fetchApi<CiLogFile[]>(
      `/api/criu-ci/runs/${encodeURIComponent(runId)}/tests/${encodeURIComponent(testName)}/${encodeURIComponent(version)}/logs`,
    ),
  getCiTestLogUrl: (runId: string, testName: string, version: string, filePath: string) =>
    `/api/criu-ci/runs/${encodeURIComponent(runId)}/tests/${encodeURIComponent(testName)}/${encodeURIComponent(version)}/logs/${filePath}`,
  listCiRunNodeLogs: (runId: string, nodeName: string) =>
    fetchApi<CiLogFile[]>(
      `/api/criu-ci/runs/${encodeURIComponent(runId)}/nodes/${encodeURIComponent(nodeName)}/logs`,
    ),
  getCiRunNodeLogUrl: (runId: string, nodeName: string, filePath: string) =>
    `/api/criu-ci/runs/${encodeURIComponent(runId)}/nodes/${encodeURIComponent(nodeName)}/logs/${filePath}`,

  // Cloud Spend
  getDailySpend: (date: string) => fetchApi<DailySpendEntry[]>("/api/spend/daily", { date }),
  getSpendSummary: (start: string, end: string) =>
    fetchApi<SpendSummary>("/api/spend/summary", { start, end }),
  getSpendTrend: (params: { days?: string; start?: string; end?: string }) =>
    fetchApi<SpendTrendPoint[]>("/api/spend/trend", params as Record<string, string>),
  triggerBackfill: (start: string, end: string) =>
    postApi<{ ok: boolean; message: string }>("/api/spend/backfill", { start, end }),

  // Admin Settings
  getIdleSpotReaper: () => fetchApi<IdleSpotReaperState>("/api/admin-settings/idle-spot-reaper"),
  setIdleSpotReaper: (enabled: boolean) =>
    putApi<IdleSpotReaperState>("/api/admin-settings/idle-spot-reaper", { enabled }),
  getNightlyReaper: () => fetchApi<NightlyReaperState>("/api/admin-settings/nightly-reaper"),
  setNightlyReaper: (patch: { enabled?: boolean; dry_run?: boolean }) =>
    putApi<NightlyReaperState>("/api/admin-settings/nightly-reaper", patch),
  getSpotPoolAccess: () => fetchApi<SpotPoolAccessState>("/api/admin-settings/spot-pool-access"),
  setSpotPoolAccess: (openToAll: boolean) =>
    putApi<SpotPoolAccessState>("/api/admin-settings/spot-pool-access", {
      open_to_all: openToAll,
    }),
  getNebiusRegions: () => fetchApi<NebiusRegionsState>("/api/admin-settings/nebius-regions"),
  setNebiusRegions: (enabled: string[]) =>
    putApi<NebiusRegionsState>("/api/admin-settings/nebius-regions", { enabled }),

  // Production Machines registry
  listProductionMachines: () => fetchApi<ProductionMachine[]>("/api/production-machines"),
  createProductionMachine: (body: CreateProductionMachineRequest) =>
    postApi<ProductionMachine>("/api/production-machines", body),
  updateProductionMachine: (id: number, body: UpdateProductionMachineRequest) =>
    patchApi<ProductionMachine>(`/api/production-machines/${id}`, body),
  deleteProductionMachine: (id: number) =>
    deleteApi<{ ok: boolean }>(`/api/production-machines/${id}`),
};
